import { qk } from '@restaurant/api-client/react';
import { formatMoney, type DailySpecialDto, type MenuItemDto } from '@restaurant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';

export interface SpecialDraft {
  menuItemId: string;
  price: string; // '' = normal price
  note: string;
}

export const toDrafts = (list: DailySpecialDto[]): SpecialDraft[] =>
  list.map((s) => ({ menuItemId: s.menuItemId, price: s.specialPriceCents == null ? '' : String(s.specialPriceCents / 100), note: s.note ?? '' }));

/** Today's (or any date's) specials: highlighted to staff and the AI, optionally at a lower price for that day. */
export function SpecialsCard({
  date,
  onDateChange,
  drafts,
  setDrafts,
  saved,
  items,
  currency,
  canEdit,
}: {
  date: string;
  onDateChange: (d: string) => void;
  drafts: SpecialDraft[];
  setDrafts: (d: SpecialDraft[]) => void;
  saved: DailySpecialDto[];
  items: Map<string, MenuItemDto>;
  currency: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(drafts) !== JSON.stringify(toDrafts(saved));

  const save = async () => {
    const bad = drafts.find((d) => d.price !== '' && !(Number(d.price) >= 0));
    if (bad) return toast.error(`Check the special price for ${items.get(bad.menuItemId)?.name ?? 'an item'}`);
    setBusy(true);
    try {
      await api.specials.set(date, {
        items: drafts.map((d) => ({ menuItemId: d.menuItemId, specialPriceCents: d.price === '' ? null : Math.round(Number(d.price) * 100), note: d.note.trim() || null })),
      });
      await qc.invalidateQueries({ queryKey: qk.specialsAll });
      toast.success('Specials saved');
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mx-auto mb-6 max-w-4xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-amber-50 px-4 py-2.5 dark:bg-amber-950/30">
        <h2 className="flex items-center gap-2 font-semibold">
          <Star className="size-4 fill-amber-400 text-amber-500" /> Specials
          <span className="text-sm font-normal text-muted-foreground">· {drafts.length}</span>
        </h2>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => onDateChange(e.target.value)} className="w-40" aria-label="Specials date" />
          {canEdit ? (
            <Button size="sm" onClick={() => void save()} disabled={!dirty || busy}>
              Save
            </Button>
          ) : null}
        </div>
      </div>
      {drafts.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">
          {canEdit ? 'No specials for this day. Tap the star next to a menu item to add it.' : 'No specials for this day.'}
        </p>
      ) : (
        <ul>
          {drafts.map((d, idx) => {
            const item = items.get(d.menuItemId);
            const set = (patch: Partial<SpecialDraft>) => setDrafts(drafts.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
            return (
              <li key={d.menuItemId} className="flex flex-wrap items-center gap-2 border-b px-4 py-2 last:border-0">
                <div className="min-w-40 flex-1">
                  <p className="truncate text-sm font-medium">{item?.name ?? 'Removed item'}</p>
                  <p className="text-xs text-muted-foreground">Menu price {item ? formatMoney(item.priceCents, currency) : '-'}</p>
                </div>
                <Input
                  value={d.price}
                  onChange={(e) => set({ price: e.target.value })}
                  inputMode="decimal"
                  placeholder="Special price (optional)"
                  className="w-44"
                  disabled={!canEdit}
                  aria-label="Special price"
                />
                <Input value={d.note} onChange={(e) => set({ note: e.target.value })} placeholder="Note, e.g. Friday only" className="w-52" disabled={!canEdit} aria-label="Note" maxLength={200} />
                {canEdit ? (
                  <Button size="icon" variant="ghost" aria-label="Remove special" onClick={() => setDrafts(drafts.filter((_, i) => i !== idx))}>
                    <X />
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/** Keeps local edits in sync with the server list whenever the date or the saved data changes. */
export function useSpecialDrafts(saved: DailySpecialDto[] | undefined, date: string) {
  const [drafts, setDrafts] = useState<SpecialDraft[]>([]);
  useEffect(() => setDrafts(toDrafts(saved ?? [])), [saved, date]);
  return [drafts, setDrafts] as const;
}
